import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI, usersAPI, nonprofitsAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Save, Zap, Heart, Users, Crown, Building, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LEAD_SOURCES = [
  'Referral',
  'Website',
  'Door-to-Door',
  'Trade Show',
  'Social Media',
  'Google Ads',
  'Partner',
  'Other',
];

const REP_TIERS = ['Broker 1', 'Broker 2', 'Manager', 'Director', 'Retail Rep'];

const TIER_RATES = {
  'Broker 1': 100,
  'Broker 2': 200,
  'Manager': 350,
  'Director': 350,
  'Retail Rep': 50,
  'Free DC Solar': 200,
};

const LeadForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [salesReps, setSalesReps] = useState([]);
  const [nonprofits, setNonprofits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [donationAmount, setDonationAmount] = useState(500);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    property_address: '',
    city: '',
    state: '',
    zip_code: '',
    lead_type: 'Residential',
    system_size_kw: 0,
    ppw_sold: '',  // Price Per Watt the deal was sold at (for Red Line)
    assigned_sales_rep_id: '',
    nonprofit_partner_id: '',
    nonprofit_eligible: false,
    notes: '',
    lead_source: '',
    // Commission fields
    seller_tier: 'Broker 1',
    is_self_sold: true,
    manager_id: '',
    director_id: '',
    is_dc_market: false,
    is_free_dc_solar: false,
    vendor_id: '',
  });

  useEffect(() => {
    loadFormData();
  }, [id]);

  const loadFormData = async () => {
    try {
      const [repsRes, nonprofitsRes, vendorsRes, usersRes, commissionRes] = await Promise.all([
        usersAPI.getSalesReps(),
        nonprofitsAPI.getAll(),
        api.get('/vendors?active_only=true'),
        isAdmin() ? usersAPI.getAll() : Promise.resolve({ data: [] }),
        api.get('/commission-structure'),
      ]);
      setSalesReps(repsRes.data);
      setNonprofits(nonprofitsRes.data);
      setVendors(vendorsRes.data);
      setAllUsers(usersRes.data);
      setDonationAmount(commissionRes.data.nonprofit_donation || 500);

      if (isEdit) {
        const leadRes = await leadsAPI.getById(id);
        setFormData({
          customer_name: leadRes.data.customer_name || '',
          phone: leadRes.data.phone || '',
          email: leadRes.data.email || '',
          property_address: leadRes.data.property_address || '',
          city: leadRes.data.city || '',
          state: leadRes.data.state || '',
          zip_code: leadRes.data.zip_code || '',
          lead_type: leadRes.data.lead_type || 'Residential',
          system_size_kw: leadRes.data.system_size_kw || 0,
          ppw_sold: leadRes.data.ppw_sold != null ? String(leadRes.data.ppw_sold) : '',
          assigned_sales_rep_id: leadRes.data.assigned_sales_rep_id || '',
          nonprofit_partner_id: leadRes.data.nonprofit_partner_id || '',
          nonprofit_eligible: leadRes.data.nonprofit_eligible || false,
          notes: leadRes.data.notes || '',
          lead_source: leadRes.data.lead_source || '',
          seller_tier: leadRes.data.seller_tier || 'Broker 1',
          is_self_sold: leadRes.data.is_self_sold !== false,
          manager_id: leadRes.data.manager_id || '',
          director_id: leadRes.data.director_id || '',
          is_dc_market: leadRes.data.is_dc_market || false,
          is_free_dc_solar: leadRes.data.is_free_dc_solar || false,
          vendor_id: leadRes.data.vendor_id || '',
        });
      }
    } catch (error) {
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // If DC market is turned off, also turn off Free DC Solar
      if (field === 'is_dc_market' && !value) {
        newData.is_free_dc_solar = false;
      }
      return newData;
    });
  };

  // When sales rep changes, auto-populate their tier and management chain
  const handleRepChange = (repId) => {
    const rep = salesReps.find(r => r.id === repId);
    if (rep) {
      setFormData(prev => ({
        ...prev,
        assigned_sales_rep_id: repId,
        seller_tier: rep.rep_tier || 'Broker 1',
        manager_id: rep.manager_id || prev.manager_id,
        director_id: rep.director_id || prev.director_id,
      }));
    } else {
      handleChange('assigned_sales_rep_id', repId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        system_size_kw: parseFloat(formData.system_size_kw) || 0,
        ppw_sold: formData.ppw_sold ? parseFloat(formData.ppw_sold) : null,  // Price Per Watt for Red Line
        assigned_sales_rep_id: formData.assigned_sales_rep_id || null,
        nonprofit_partner_id: formData.nonprofit_eligible ? formData.nonprofit_partner_id || null : null,
        manager_id: formData.manager_id || null,
        director_id: formData.director_id || null,
        vendor_id: formData.vendor_id || null,
      };

      if (isEdit) {
        await leadsAPI.update(id, submitData);
        toast.success('Lead updated successfully');
      } else {
        await leadsAPI.create(submitData);
        toast.success('Lead created successfully');
      }
      navigate('/leads');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save lead';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Calculate commission preview based on tier
  const isFreeDC = formData.is_dc_market && formData.is_free_dc_solar;
  const tierRate = isFreeDC ? 200 : (TIER_RATES[formData.seller_tier] || 100);
  const kw = parseFloat(formData.system_size_kw) || 0;
  const repCommission = kw * tierRate;
  
  // Nonprofit donation (deducted from rep commission)
  const nonprofitDonation = formData.nonprofit_eligible && formData.nonprofit_partner_id ? donationAmount : 0;
  const netRepCommission = Math.max(0, repCommission - nonprofitDonation);
  
  // Manager override preview
  let managerOverride = 0;
  if (formData.manager_id && formData.seller_tier !== 'Manager' && formData.seller_tier !== 'Director') {
    if (formData.seller_tier === 'Broker 1') managerOverride = kw * 250;
    else if (formData.seller_tier === 'Broker 2') managerOverride = kw * 150;
    else if (formData.seller_tier === 'Retail Rep' && formData.is_dc_market) managerOverride = kw * 75;
  }
  
  // Director override preview
  let directorOverride = 0;
  if (formData.director_id) {
    if (formData.seller_tier === 'Manager') directorOverride = kw * 50;
    else if (!formData.manager_id) {
      if (formData.seller_tier === 'Broker 1') directorOverride = kw * 250;
      else if (formData.seller_tier === 'Broker 2') directorOverride = kw * 150;
    }
  }
  
  const totalPayout = netRepCommission + managerOverride + directorOverride + nonprofitDonation;

  // Get managers and directors for selection
  const managers = allUsers.filter(u => u.rep_tier === 'Manager' && u.is_approved);
  const directors = allUsers.filter(u => u.rep_tier === 'Director' && u.is_approved);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="lead-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            {isEdit ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {isEdit ? 'Update lead information' : 'Capture a new solar lead'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Customer Information</CardTitle>
            <CardDescription>Basic contact details for the lead</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_name">Customer Name *</Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                required
                placeholder="John Smith"
                data-testid="customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                placeholder="(555) 123-4567"
                data-testid="phone-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                placeholder="john@example.com"
                data-testid="email-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Property Information */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Property Information</CardTitle>
            <CardDescription>Location details for the installation</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="property_address">Property Address *</Label>
              <Input
                id="property_address"
                value={formData.property_address}
                onChange={(e) => handleChange('property_address', e.target.value)}
                required
                placeholder="123 Main Street"
                data-testid="address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                required
                placeholder="Phoenix"
                data-testid="city-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  required
                  placeholder="AZ"
                  maxLength={2}
                  data-testid="state-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code *</Label>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  required
                  placeholder="85001"
                  data-testid="zip-input"
                />
              </div>
            </div>
            
            {/* DC Market Toggle */}
            <div className="flex items-center justify-between md:col-span-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              <div>
                <Label>DC Market</Label>
                <p className="text-sm text-slate-500">Is this property in the DC market?</p>
              </div>
              <Switch
                checked={formData.is_dc_market}
                onCheckedChange={(checked) => handleChange('is_dc_market', checked)}
                data-testid="dc-market-switch"
              />
            </div>
            
            {/* Free DC Solar Toggle - Only shown when DC Market is enabled */}
            {formData.is_dc_market && (
              <div className="flex items-center justify-between md:col-span-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 animate-fade-in">
                <div>
                  <Label className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-blue-500" />
                    FREE DC Solar
                  </Label>
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    Special $200/kW rate for Free DC Solar program
                  </p>
                </div>
                <Switch
                  checked={formData.is_free_dc_solar}
                  onCheckedChange={(checked) => handleChange('is_free_dc_solar', checked)}
                  data-testid="free-dc-solar-switch"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deal Information */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Deal Information</CardTitle>
            <CardDescription>System details and assignments</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lead_type">Lead Type *</Label>
              <Select
                value={formData.lead_type}
                onValueChange={(value) => handleChange('lead_type', value)}
              >
                <SelectTrigger data-testid="lead-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="system_size_kw">Estimated System Size (kW)</Label>
              <div className="relative">
                <Input
                  id="system_size_kw"
                  type="number"
                  step="0.001"
                  min="0"
                  value={formData.system_size_kw}
                  onChange={(e) => handleChange('system_size_kw', e.target.value)}
                  placeholder="10.564"
                  className="pr-12"
                  data-testid="system-size-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  kW
                </span>
              </div>
            </div>
            
            {/* Price Per Watt - For Red Line Commission */}
            <div className="space-y-2">
              <Label htmlFor="ppw_sold" className="flex items-center gap-2">
                Deal Price Per Watt ($/W)
                <span className="text-xs text-slate-400 font-normal">(for Red Line)</span>
              </Label>
              <div className="relative">
                <Input
                  id="ppw_sold"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.ppw_sold}
                  onChange={(e) => handleChange('ppw_sold', e.target.value)}
                  placeholder="e.g., 3.00"
                  className="pr-12"
                  data-testid="ppw-sold-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  $/W
                </span>
              </div>
              <p className="text-xs text-slate-500">Required for Director Red Line commission calculation</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assigned_sales_rep_id">Assigned Sales Rep</Label>
              <Select
                value={formData.assigned_sales_rep_id}
                onValueChange={handleRepChange}
              >
                <SelectTrigger data-testid="sales-rep-select">
                  <SelectValue placeholder="Select sales rep" />
                </SelectTrigger>
                <SelectContent>
                  {salesReps.map((rep) => (
                    <SelectItem key={rep.id} value={rep.id}>
                      {rep.name} {rep.rep_tier && <span className="text-slate-400">({rep.rep_tier})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead_source">Lead Source</Label>
              <Select
                value={formData.lead_source}
                onValueChange={(value) => handleChange('lead_source', value)}
              >
                <SelectTrigger data-testid="lead-source-select">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Commission Structure - Admin Only */}
        {isAdmin() && (
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Commission Structure
                <Badge variant="secondary" className="ml-2">Admin Only</Badge>
              </CardTitle>
              <CardDescription>Configure seller tier and management chain for payout calculations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Seller Tier *</Label>
                  <Select
                    value={formData.seller_tier}
                    onValueChange={(value) => handleChange('seller_tier', value)}
                  >
                    <SelectTrigger data-testid="seller-tier-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REP_TIERS.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier} - ${TIER_RATES[tier]}/kW
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div>
                    <Label>Self-Sold</Label>
                    <p className="text-sm text-slate-500">Rep sold this deal themselves</p>
                  </div>
                  <Switch
                    checked={formData.is_self_sold}
                    onCheckedChange={(checked) => handleChange('is_self_sold', checked)}
                    data-testid="self-sold-switch"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" />
                    Manager (for override)
                  </Label>
                  <Select
                    value={formData.manager_id || "none"}
                    onValueChange={(value) => handleChange('manager_id', value === "none" ? "" : value)}
                  >
                    <SelectTrigger data-testid="manager-select">
                      <SelectValue placeholder="Select manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Manager</SelectItem>
                      {managers.map((mgr) => (
                        <SelectItem key={mgr.id} value={mgr.id}>
                          {mgr.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-emerald-500" />
                    Director (for override)
                  </Label>
                  <Select
                    value={formData.director_id || "none"}
                    onValueChange={(value) => handleChange('director_id', value === "none" ? "" : value)}
                  >
                    <SelectTrigger data-testid="director-select">
                      <SelectValue placeholder="Select director" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Director</SelectItem>
                      {directors.map((dir) => (
                        <SelectItem key={dir.id} value={dir.id}>
                          {dir.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vendor - Admin Only sees payout info */}
        {vendors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-500" />
                Vendor Partner
              </CardTitle>
              <CardDescription>Associate a vendor with this deal (optional)</CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.vendor_id || "none"}
                onValueChange={(value) => handleChange('vendor_id', value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="vendor-select">
                  <SelectValue placeholder="Select vendor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Vendor</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name} ({vendor.vendor_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* SUNRAISING Program */}
        <Card className="border-amber-200 dark:border-amber-800">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              SUNRAISING Program
            </CardTitle>
            <CardDescription>
              Enable nonprofit donation for this lead (${donationAmount} per completed sale, deducted from rep commission)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="nonprofit_eligible">Enable SUNRAISING</Label>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Donate ${donationAmount} to a nonprofit (deducted from rep commission)
                </p>
              </div>
              <Switch
                id="nonprofit_eligible"
                checked={formData.nonprofit_eligible}
                onCheckedChange={(checked) => handleChange('nonprofit_eligible', checked)}
                data-testid="nonprofit-switch"
              />
            </div>
            {formData.nonprofit_eligible && (
              <div className="space-y-2 animate-fade-in">
                <Label htmlFor="nonprofit_partner_id">Nonprofit Partner *</Label>
                <Select
                  value={formData.nonprofit_partner_id}
                  onValueChange={(value) => handleChange('nonprofit_partner_id', value)}
                >
                  <SelectTrigger data-testid="nonprofit-select">
                    <SelectValue placeholder="Select nonprofit" />
                  </SelectTrigger>
                  <SelectContent>
                    {nonprofits.map((np) => (
                      <SelectItem key={np.id} value={np.id}>
                        {np.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {nonprofits.length === 0 && (
                  <p className="text-sm text-amber-600">
                    No nonprofits available. Please add one first.
                  </p>
                )}
                {formData.nonprofit_partner_id && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700 dark:text-amber-300">
                      ${donationAmount} will be deducted from the rep commission for this donation.
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any additional notes about this lead..."
              rows={4}
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* Payout Preview */}
        <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
          <CardHeader>
            <CardTitle className="font-heading text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Payout Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">Gross Rep Commission</p>
                <p className="text-xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                  ${repCommission.toLocaleString()}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  {kw} kW × ${tierRate}/kW {isFreeDC ? '(Free DC Solar)' : `(${formData.seller_tier})`}
                </p>
              </div>
              {nonprofitDonation > 0 && (
                <div>
                  <p className="text-sm text-rose-700 dark:text-rose-300">Donation Deduction</p>
                  <p className="text-xl font-bold font-mono text-rose-600 dark:text-rose-400">
                    -${nonprofitDonation.toLocaleString()}
                  </p>
                  <p className="text-xs text-rose-500">From rep commission</p>
                </div>
              )}
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">Net Rep Commission</p>
                <p className="text-xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                  ${netRepCommission.toLocaleString()}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">After donation</p>
              </div>
              {managerOverride > 0 && (
                <div>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">Manager Override</p>
                  <p className="text-xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                    ${managerOverride.toLocaleString()}
                  </p>
                </div>
              )}
              {directorOverride > 0 && (
                <div>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">Director Override</p>
                  <p className="text-xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                    ${directorOverride.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-emerald-300 dark:border-emerald-700">
              <div className="flex items-center justify-between">
                <span className="font-medium text-emerald-800 dark:text-emerald-200">Total Estimated Payout</span>
                <span className="text-2xl font-bold font-mono text-emerald-800 dark:text-emerald-200">
                  ${totalPayout.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20"
            disabled={saving}
            data-testid="save-lead-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Update Lead' : 'Create Lead'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
